// lib/summaryGenerator.ts
import { supabase, supabaseAnonKey } from '@/lib/supabase';

export interface SummaryResult {
  summary: string;
  keyPoints: string[];
  topics: string[];
  actionItems: string[];
}

/**
 * Generate AI summary from transcript or document text
 */
export async function generateSummary(text: string, title: string): Promise<string> {
  try {
    const functionAuthHeaders = {
      Authorization: `Bearer ${supabaseAnonKey}`,
      apikey: supabaseAnonKey,
    };

    const summaryPrompt = createSummaryPrompt(text, title);
    
    // Call the correct whipt-ai-summary function
    const response = await supabase.functions.invoke('whipt-ai-summary', {
      headers: functionAuthHeaders,
      body: {
        text: text,
        prompt: summaryPrompt,
      },
    });

    if (response.error) {
      throw new Error(response.error.message || 'Summary generation failed');
    }

    const summary = response.data?.summary || 'No summary generated';
    return summary;
  } catch (error) {
    console.error('Error generating summary:', error);
    throw error;
  }
}

/**
 * Format summary prompt for AI - Enhanced for scholarly/doctoral work
 */
export function createSummaryPrompt(text: string, title: string): string {
  // Check if this looks like academic content
  const isAcademic = /abstract|methodology|introduction|literature review|discussion|conclusion|references|citation/i.test(text.substring(0, 2000));
  
  if (isAcademic) {
    return `Please create an academic summary of the following scholarly document titled "${title}".

Format the summary using ONLY these section headers with a colon, followed by the content. Add blank lines between sections:

Background: [Describe context, prior research, and problem]

Objectives: [State research objectives or hypotheses]

Methods: [Describe study design, participants, measures, and procedures]

Results: [Present main findings with specific statistics]

Conclusions: [State main conclusions and implications]

IMPORTANT:
- Start each section with the bolded label followed by a colon
- Write each section as a flowing paragraph
- Include specific numbers, statistics, and effect sizes in Results
- Add a blank line between each section
- Do NOT use bullet points
- Do NOT add extra headers or sections

Provide a structured analysis in this EXACT format:

---

Content to analyze:
${text.substring(0, 50000)} ${text.length > 50000 ? '\n\n[Content truncated due to length]' : ''}`;
  }
  
  // Default format for non-academic content
  return `Please create a comprehensive summary of the following ${title ? `document titled "${title}"` : 'content'}:

Provide the summary in this structured format:

# Key Points:
• [List 3-5 main points as bullet points]

# Main Topics:
• [List major topics discussed]

# Action Items:
• [List any tasks, assignments, or deadlines mentioned]

# Important Information:
• [List any key dates, names, numbers, or critical facts]

---

Content to summarize:
${text.substring(0, 50000)} ${text.length > 50000 ? '\n\n[Content truncated due to length]' : ''}`;
}
